import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';
import { Button } from '@common/components/Button';
import { Flex } from '@common/components/Flex';
import Tree, { TreeSelectionType, getItem, flattenTree } from '@common/components/Tree';
import { MODULE_NAME } from '../../audit-report';
import { getFields, updateFieldsToAudit, ENTITY_TYPE_TYPE } from '../../actions/main';
import { getTestAttributes } from '@common/utils/tests';

import './fields-editing.styles.scss';

export const FieldsEditing = (props) => {
  const [loadingData, setLoadingDataState] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [data, setData] = React.useState([]);
  const [value, setValue] = React.useState([]);
  const [initialValue, setInitialValue] = React.useState([]);
  const { close, onSuccessSubmit, showMessage } = props;
  const flattenData = React.useMemo(() => {
    return flattenTree(data);
  }, [data]);

  React.useEffect(() => {
    setLoadingDataState(true);

    getFields(data)
      .then((data) => {
        setData(data);
        const selectedItems = flattenTree(data).reduce((acc, x) => {
          if (x.auditOn) {
            acc.push(x.id);
          }

          return acc;
        }, []);

        setValue(selectedItems);
        setInitialValue(selectedItems);
      })
      .catch((err) => {
        console.error(err);
        showMessage({ text: I18n.t('Failed to get fields'), size: 'middle' });
      })
      .finally(() => {
        setLoadingDataState(false);
      });
  }, []);

  const handleSubmit = React.useCallback(async () => {
    try {
      setSubmitting(true);

      const selectedIds = value.filter((id) => !initialValue.includes(id));
      const unSelectedIds = initialValue.filter((id) => !value.includes(id));

      const changedItems = [...selectedIds, ...unSelectedIds].reduce((acc, id) => {
        const item = getItem(id, flattenData, { dataIsFlatten: true });

        if (item.type === ENTITY_TYPE_TYPE.field) {
          acc.push({
            fieldId: item.entityId,
            dataFolderId: item.dataFolderId,
            context: item.context,
            auditOn: selectedIds.includes(id)
          });
        }

        return acc;
      }, []);

      await updateFieldsToAudit(changedItems);
      await onSuccessSubmit();
    } catch (err) {
      console.error(err);
      showMessage({ text: I18n.t('Failed to save result'), size: 'middle' });
      setSubmitting(false);
    }
  }, [value, initialValue, flattenData]);

  const handleSelectAll = React.useCallback(() => {
    setValue(flattenData.map((x) => x.id));
  }, [flattenData]);

  const handleClearAll = React.useCallback(() => {
    setValue([]);
  }, []);

  return ( 
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="FieldAuditReportFieldsEditing"
      animation={false}
      onEscapeKeyDown={close}
      onHide={close}
    >
      <ModalHeader closeButton onHide={close} className="FieldAuditReportFieldsEditing__header">
        <ModalTitle {...getTestAttributes(MODULE_NAME, 'edit-fields', 'header')}>
          {I18n.t('Audit Fields')}
        </ModalTitle>
      </ModalHeader>

      <ModalBody className="FieldAuditReportFieldsEditing__content">
        <Flex justify="flex-end" className="FieldAuditReportFieldsEditing__content__header">
          <Button 
            disabled={submitting || loadingData}
            {...getTestAttributes(MODULE_NAME, 'edit-fields-select-all', 'button')}
            onClick={handleSelectAll}
          >
            {I18n.t('Select All')}
          </Button>
          <Button 
            disabled={submitting || loadingData}
            {...getTestAttributes(MODULE_NAME, 'edit-fields-clear-all', 'button')}
            onClick={handleClearAll}
          >
            {I18n.t('Clear All')}
          </Button>
        </Flex>
        {!loadingData && Boolean(data.length) && (
          <Tree
            multiple
            collapseAfterLevel={1}
            normalizeId={String}
            selectionType={TreeSelectionType.parentChild}
            className="FieldAuditReportFieldsEditing__content__tree"
            features={{
              checkbox: true
            }}
            items={data}
            value={value}
            onChange={setValue} 
          />
        )}
        {loadingData && (
          <Flex justify="center" alignItems="center" className="FieldAuditReportFieldsEditing__content__loading">
            {I18n.t('Loading')}...
          </Flex>
        )}
        <Flex justify="flex-end" className="FieldAuditReportFieldsEditing__content__footer">
          <Button 
            disabled={submitting}
            {...getTestAttributes(MODULE_NAME, 'edit-fields-cancel', 'button')}
            onClick={close}
          >
            {I18n.t('Cancel')}
          </Button>
          <Button 
            bsStyle="primary"
            disabled={submitting || loadingData}
            {...getTestAttributes(MODULE_NAME, 'edit-fields-submit', 'button')}
            onClick={handleSubmit}
          >
            {submitting ? I18n.t('Loading') : I18n.t('Ok')}
          </Button>
        </Flex>
      </ModalBody>
    </Modal>
  );
};